import { Form } from 'antd';
import { LoginPayload } from '@/services/authentication-service/authentication.service.types';
import { Button } from '../button/button';
import styles from './sign-in-form.module.scss';
import TextField from '../form/text-field/text-field';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

export type FieldType = {
  email: string;
  password: string;
};

interface SignInFormProps {
  onSubmit: (values: FieldType) => void;
  errors?: string[] | string;
  isLoading?: boolean;
}

export function SignInForm({ onSubmit, errors, isLoading }: SignInFormProps) {
  const { t } = useTranslation(['auth', 'common']);
  const [showError, setShowError] = useState(!!errors);
  const [emailErrors, setEmailErrors] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (errors) {
      setShowError(!!errors);
    }
  }, [errors]);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      onFieldsChange={() => {
        setShowError(false);
        setEmailErrors(!!form.getFieldError('email')[0]);
      }}
      autoComplete="off"
      className={styles.formContainer}
      requiredMark="optional"
      layout="vertical"
    >
      <Form.Item<FieldType>
        label={t('auth:fields.email')}
        name="email"
        htmlFor="email-input"
        rules={[
          { required: true, message: t('common:validation.emailRequired') },
          {
            type: 'email',
            message: t('common:validation.emailInvalid'),
          },
        ]}
      >
        <TextField
          status={showError || emailErrors ? 'error' : undefined}
          data-testid="email"
          id="email-input"
          type="email"
          autoComplete="email"
          onPaste={(e) => {
            const pastedValue = e.clipboardData.getData('Text');
            form.setFieldsValue({ email: pastedValue.trim() });
            e.preventDefault();
          }}
        />
      </Form.Item>

      <Form.Item<LoginPayload>
        label={t('auth:fields.password')}
        name="password"
        htmlFor="password-input"
        className={styles.passwordContainer}
        rules={[{ required: true, message: t('common:validation.passwordRequired') }]}
        shouldUpdate
      >
        <TextField
          status={showError ? 'error' : undefined}
          data-testid="password"
          type="password"
        />
      </Form.Item>
      <Link to="/forgot-password" className={styles.link}>
        {t('auth:signIn.forgotPassword')}
      </Link>

      <div className={styles.errorContainer}>
        {showError && (
          <span>
            {typeof errors === 'string' ? (
              <p className={styles.error}>{errors}</p>
            ) : (
              errors?.map((error, index) => (
                <p key={index} className={styles.error}>
                  {error}
                </p>
              ))
            )}
          </span>
        )}
      </div>

      <Form.Item className={styles.submitButton}>
        <Button
          size="large"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          block
          disabled={showError}
        >
          {t('auth:signIn.submit')}
        </Button>
      </Form.Item>

      <p className={styles.signUpLink}>
        <Trans ns="auth" i18nKey="signIn.noAccount" components={{ link: <Link to="/sign-up" /> }} />
      </p>
      <p className={styles.signUpLink}>
        <Trans ns="auth" i18nKey="signIn.support" components={{ link: <Link to="/support-ticket" /> }} />
      </p>
    </Form>
  );
}
